import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import React, { useState, useRef, useEffect } from 'react';
import { useFormik } from 'formik';
import { Api } from '../../services/api.js';
import { Toast } from 'primereact/toast';
import './BuilderForm.css';

// ── Replace this with your actual Calendly / booking page URL ──────────────────
const BOOKING_URL = 'https://outlook.office.com/book/BookingsTerry@lidaconstruction.ca';
// ─────────────────────────────────────────────────────────────────────────────

const PROJECT_TYPES = [
  { value: 'Custom Home',                   group: 'custom' },
  { value: 'Major Renovation / Addition',   group: 'renovation' },
  { value: 'Kitchen Renovation',            group: 'renovation' },
  { value: 'Whole Home Renovation',         group: 'renovation' },
  { value: 'Garden Suite / Carriage House', group: 'gardensuite' },
  { value: 'House Lift',                    group: 'renovation' },
  { value: 'Multi-Family',                  group: 'multifamily' },
  { value: 'Commercial',                    group: 'multifamily' },
  { value: 'Interior Design Only',          group: 'renovation' },
  { value: 'Other',                         group: 'other' },
];

const CRM_PROJECT_TYPE_MAP = {
  'Custom Home':                   'Custom Home',
  'Major Renovation / Addition':   'Renovation',
  'Kitchen Renovation':            'Renovation',
  'Whole Home Renovation':         'Renovation',
  'Garden Suite / Carriage House': 'Custom Home',
  'House Lift':                    'Renovation',
  'Multi-Family':                  'Multi Family',
  'Commercial':                    'Commercial',
  'Interior Design Only':          'Interior Design Only',
  'Other':                         'Not Collected',
};

const BUDGET_RANGES = {
  custom:      ['$1M – $1.5M', '$1.5M – $3M', '$3M+'],
  gardensuite: ['$350K – $450K', '$450K – $600K', '$600K+'],
  renovation:  ['$100K – $250K', '$250K – $500K', '$500K+'],
  multifamily: ['$1M – $3M', '$3M – $5M', '$5M – $10M', '$10M+'],
  other:       ['$100K – $250K', '$250K – $500K', '$500K – $1M', '$1M+'],
};

const CONSTRUCTION_TIMELINES = ['0–6 months', '6–12 months', '12+ months', 'Just exploring timelines'];

const PLANNING_STAGES = [
  'Just exploring ideas',
  'Own property / home',
  'Working with a designer or architect',
  'Ready to begin planning',
  'Ready to start construction',
];

const LEAD_SOURCES = [
  'Google', 'Job Site Sign', 'Referral', 'Office Sign', 'Houzz',
  'Door Knocker', 'LinkedIn', 'Facebook', 'Instagram', 'LIDA Truck',
  'Awards', 'Magazine', 'BBB', '3 Best Rated', 'Previous Client',
  'Personal Contact', 'Other', 'Chek TV', 'Youtube', 'Newspaper Ad',
  'Online Ad', 'Radio Ad', 'Event', 'Permit Tracker',
];

function BuilderForm() {
  const [isLoading, setIsLoading]       = useState(false);
  const [isSubmitted, setIsSubmitted]   = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const toast = useRef(null);

  // Ensure the page scrolls when embedded standalone (no Layout wrapper)
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
    };
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      first_name:            '',
      last_name:             '',
      email:                 '',
      phone_number:          '',
      street_number:         '',
      street_name:           '',
      city:                  '',
      project_type:          '',
      project_value:         '',
      construction_timeline: '',
      others_involved:       '',
      planning_stage:        '',
      project_desc:          '',
      lead_source:           '',
      who_referred_you:      '',
      agree_to_contact:      false,
    },
    validate: (data) => {
      const errors = {};
      if (!data.project_desc || !String(data.project_desc).trim()) {
        errors.project_desc = 'Project description is required.';
      }
      if (!data.agree_to_contact) {
        errors.agree_to_contact = 'You must agree to be contacted.';
      }
      return errors;
    },
    onSubmit: (data) => {
      setIsLoading(true);
      setErrorMessage('');

      // Construct the full address from number and street name for the CRM
      const payload = {
        ...data,
        address: `${data.street_number} ${data.street_name}`.trim(),
        project_type: CRM_PROJECT_TYPE_MAP[data.project_type] || data.project_type,
      };

      fetch(Api.API_BASE_URL + 'crm/insert-lead-crm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((json) => {
          setIsLoading(false);
          if (json.error && json.error !== '') {
            setErrorMessage(json.error);
          } else {
            setIsSubmitted(true);
            formik.resetForm();
          }
        })
        .catch((err) => {
          console.error('Error:', err);
          setIsLoading(false);
          setErrorMessage('Something went wrong. Please try again.');
        });
    },
  });

  const selectedGroup = PROJECT_TYPES.find(p => p.value === formik.values.project_type)?.group;
  const budgetOptions = selectedGroup ? BUDGET_RANGES[selectedGroup] : [];

  // ── Post-submission confirmation screen ──────────────────────────────────
  if (isSubmitted) {
    return (
      <div className="Builder" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
        <div className="bf-confirmation">
          <div className="bf-confirmation-icon">✓</div>
          <h2>Thank you — your project inquiry has been received.</h2>
          <p>
            The next step is to schedule a brief phone consultation so we can learn more about
            your project and answer any initial questions.
          </p>
          <p><strong>Please choose a time that works best for you.</strong></p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bf-book-btn"
          >
            Book Your Consultation
          </a>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div className="Builder" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
      <Toast ref={toast} />

      <div className="bf-header">
        <h1 className="bf-company">LIDA Homes</h1>
        <h2 className="bf-title">Client Consultation Request Form</h2>
        <p className="bf-subtitle">
          We ask a few questions to ensure we&apos;re the right fit and to make the most of your consultation. This helps us provide accurate guidance and a better overall experience.
        </p>
      </div>

      <form onSubmit={formik.handleSubmit}>

        {/* ── SECTION 1: PROJECT TYPE ────────────────────────────────── */}
        <div className="bf-section">
          <div className="bf-section-title">Section 1 — Project Type</div>
          <div className="bf-field">
            <label className="bf-label" htmlFor="project_type">
              Project Type <span style={{ color: 'red' }}>*</span>
            </label>
            <Dropdown
              id="project_type"
              value={formik.values.project_type}
              onChange={(e) => {
                formik.setFieldValue('project_type', e.value);
                formik.setFieldValue('project_value', '');
              }}
              options={PROJECT_TYPES}
              optionLabel="value"
              optionValue="value"
              placeholder="Select a Project Type"
              className="w-full"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* ── SECTION 2: BUDGET & TIMELINE ──────────────────────────── */}
        <div className="bf-section">
          <div className="bf-section-title">Section 2 — Budget &amp; Timeline</div>

          {formik.values.project_type && (
            <div className="bf-field">
              <label className="bf-label">
                Estimated Investment Range <span style={{ color: 'red' }}>*</span>
              </label>
              <div className="bf-options-list">
                {budgetOptions.map((range) => (
                  <label key={range} className="bf-option-label">
                    <input
                      type="radio"
                      name="project_value"
                      value={range}
                      checked={formik.values.project_value === range}
                      onChange={() => formik.setFieldValue('project_value', range)}
                      required
                    />
                    <span>{range}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="bf-field">
            <label className="bf-label">
              When are you hoping to begin construction? <span style={{ color: 'red' }}>*</span>
            </label>
            <div className="bf-options-list">
              {CONSTRUCTION_TIMELINES.map((t) => (
                <label key={t} className="bf-option-label">
                  <input
                    type="radio"
                    name="construction_timeline"
                    value={t}
                    checked={formik.values.construction_timeline === t}
                    onChange={() => formik.setFieldValue('construction_timeline', t)}
                    required
                  />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bf-field">
            <label className="bf-label">
              Will others be involved in project decisions? <span style={{ color: 'red' }}>*</span>
            </label>
            <div className="bf-options-list">
              {['Yes', 'No'].map((opt) => (
                <label key={opt} className="bf-option-label">
                  <input
                    type="radio"
                    name="others_involved"
                    value={opt}
                    checked={formik.values.others_involved === opt}
                    onChange={() => formik.setFieldValue('others_involved', opt)}
                    required
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── SECTION 3: PROJECT DETAILS ────────────────────────────── */}
        <div className="bf-section">
          <div className="bf-section-title">Section 3 — Project Details</div>

          <div className="bf-field">
            <label className="bf-label">Project Location</label>
            {/* Street Number + Street Name */}
            <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <label className="bf-sublabel" htmlFor="street_number">Street Number <span style={{ color: 'red' }}>*</span></label>
                <InputText
                  id="street_number"
                  className="InputTexty w-full"
                  value={formik.values.street_number}
                  onChange={(e) => formik.setFieldValue('street_number', e.target.value)}
                  type="text"
                  required
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="bf-sublabel" htmlFor="street_name">Street Name <span style={{ color: 'red' }}>*</span></label>
                <InputText
                  id="street_name"
                  className="InputTexty w-full"
                  value={formik.values.street_name}
                  onChange={(e) => formik.setFieldValue('street_name', e.target.value)}
                  type="text"
                  required
                />
              </div>
            </div>
            {/* Street Address + City row */}
            <div className="form-row" style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label className="bf-sublabel" htmlFor="city">City <span style={{ color: 'red' }}>*</span></label>
                <InputText
                  id="city"
                  className="InputTexty w-full"
                  value={formik.values.city}
                  onChange={(e) => formik.setFieldValue('city', e.target.value)}
                  type="text"
                  required
                />
              </div>
            </div>
          </div>

          <div className="bf-field">
            <label className="bf-label">
              Where are you in the planning process? <span style={{ color: 'red' }}>*</span>
            </label>
            <div className="bf-options-list">
              {PLANNING_STAGES.map((stage) => (
                <label key={stage} className="bf-option-label">
                  <input
                    type="radio"
                    name="planning_stage"
                    value={stage}
                    checked={formik.values.planning_stage === stage}
                    onChange={() => formik.setFieldValue('planning_stage', stage)}
                    required
                  />
                  <span>{stage}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bf-field">
            <label className="bf-label" htmlFor="projectDescription">
              Project Description <span style={{ color: 'red' }}>*</span>
            </label>
            <p className="bf-hint">Tell us a little about your project and what you&apos;re hoping to achieve.</p>
            <textarea
              id="projectDescription"
              className="bf-textarea"
              value={formik.values.project_desc}
              onChange={(e) => formik.setFieldValue('project_desc', e.target.value)}
              onBlur={() => formik.setFieldTouched('project_desc', true)}
              rows="5"
              required
            />
            {formik.errors.project_desc && formik.touched.project_desc && (
              <div className="bf-error">{formik.errors.project_desc}</div>
            )}
          </div>
        </div>

        {/* ── SECTION 4: CONTACT INFORMATION ───────────────────────── */}
        <div className="bf-section">
          <div className="bf-section-title">Section 4 — Contact Information</div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem' }}>
            <div className="bf-field" style={{ flex: 1 }}>
              <label className="bf-label" htmlFor="firstName">
                First Name <span style={{ color: 'red' }}>*</span>
              </label>
              <InputText
                id="firstName"
                className="InputTexty w-full"
                value={formik.values.first_name}
                onChange={(e) => formik.setFieldValue('first_name', e.target.value)}
                type="text"
                required
              />
            </div>
            <div className="bf-field" style={{ flex: 1 }}>
              <label className="bf-label" htmlFor="lastName">
                Last Name <span style={{ color: 'red' }}>*</span>
              </label>
              <InputText
                id="lastName"
                className="InputTexty w-full"
                value={formik.values.last_name}
                onChange={(e) => formik.setFieldValue('last_name', e.target.value)}
                type="text"
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem' }}>
            <div className="bf-field" style={{ flex: 1 }}>
              <label className="bf-label" htmlFor="email">
                Email Address <span style={{ color: 'red' }}>*</span>
              </label>
              <InputText
                id="email"
                className="InputTexty w-full"
                value={formik.values.email}
                onChange={(e) => formik.setFieldValue('email', e.target.value)}
                type="email"
                required
              />
            </div>
            <div className="bf-field" style={{ flex: 1 }}>
              <label className="bf-label" htmlFor="phone">
                Phone Number <span style={{ color: 'red' }}>*</span>
              </label>
              <InputText
                id="phone"
                className="InputTexty w-full"
                value={formik.values.phone_number}
                onChange={(e) => formik.setFieldValue('phone_number', e.target.value)}
                type="tel"
                required
              />
            </div>
          </div>

          <div className="bf-field">
            <label className="bf-option-label">
              <input
                type="checkbox"
                checked={formik.values.agree_to_contact}
                onChange={(e) => formik.setFieldValue('agree_to_contact', e.target.checked)}
                required
              />
              <span>I agree to be contacted regarding my project inquiry.</span>
            </label>
            {formik.errors.agree_to_contact && formik.touched.agree_to_contact && (
              <div className="bf-error">{formik.errors.agree_to_contact}</div>
            )}
          </div>

          <div className="bf-field">
            <label className="bf-label" htmlFor="leadSource">
              How Did You Hear About Us? <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="leadSource"
              className="bf-select"
              value={formik.values.lead_source}
              onChange={(e) => {
                formik.setFieldValue('lead_source', e.target.value);
                if (e.target.value !== 'Referral') {
                  formik.setFieldValue('who_referred_you', '');
                }
              }}
              required
            >
              <option value="">-- Select an option --</option>
              {LEAD_SOURCES.map((src) => (
                <option key={src} value={src}>{src}</option>
              ))}
            </select>
          </div>

          {formik.values.lead_source === 'Referral' && (
            <div className="bf-field">
              <label className="bf-label" htmlFor="whoReferredYou">
                Who Referred You? <span style={{ color: 'red' }}>*</span>
              </label>
              <InputText
                id="whoReferredYou"
                className="InputTexty w-full"
                value={formik.values.who_referred_you}
                onChange={(e) => formik.setFieldValue('who_referred_you', e.target.value)}
                type="text"
                required
              />
            </div>
          )}

        </div>

        {errorMessage && (
          <div className="bf-error" style={{ marginBottom: '1rem' }}>{errorMessage}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '2rem' }}>
          <button
            className="submit-btn uppercase text-white py-2 px-5 border-none"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? 'Submitting...' : 'SUBMIT'}
          </button>
        </div>

      </form>
    </div>
  );
}

export default BuilderForm;